import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import {
  ShiftNeed,
  PagedResultDto,
  AvailabilitySlot,
  ShiftRequest,
  ShiftRequestSearchDto,
  CounterOfferRequestDto,
  CounterOfferDto,
  NegotiationHistoryDto
} from '@umss/shared/data-access';
import {
  HttpHelper,
  ScopeOfPracticeEnum,
  ShiftTypeEnum
} from '@umss/shared/utils';
import {
  ShiftNeedSearchDto,
  CreateShiftRequestDto,
  ShiftRequestResponse,
  DashboardShiftRequestDto
} from '../dtos';
import { MOCK_SHIFT_NEEDS } from '../mocks/shift-needs.mock';
import { MOCK_SHIFT_REQUESTS } from '../mocks/dashboard.mock';

@Injectable({ providedIn: 'root' })
export class ShiftNeedsService {
  private readonly apiUrl = '/api/shift-needs';
  private readonly contractorsApi = '/api/contractors';

  constructor(private http: HttpClient) {}

  /**
   * Legacy method - Get all shift needs as availability slots
   * @deprecated Use searchShiftNeeds() for better filtering and pagination
   */
  getAll(payload?: Partial<ShiftRequest>): Observable<AvailabilitySlot[]> {
    const params = HttpHelper.toHttpParams(payload ?? {});
    return this.http.get<AvailabilitySlot[]>(this.apiUrl, { params });
  }

  /**
   * Search available shift needs with filters
   * Falls back to mock data if API fails
   */
  searchShiftNeeds(
    searchDto: ShiftNeedSearchDto
  ): Observable<PagedResultDto<ShiftNeed>> {
    const params = HttpHelper.toHttpParams(searchDto);

    return this.http
      .get<PagedResultDto<ShiftNeed>>(this.apiUrl, { params })
      .pipe(
        catchError(() => {
          return of(this.getMockShiftNeeds(searchDto));
        })
      );
  }

  // Get a single shift need by ID
  getShiftNeedById(id: string): Observable<ShiftNeed> {
    return this.http.get<ShiftNeed>(`${this.apiUrl}/${id}`);
  }

  /**
   * Apply for a shift (request a shift).
   * Pass requestedHourlyRate to propose a custom rate on an urgent shift.
   */
  requestShift(
    shiftNeedId: string,
    request: Omit<CreateShiftRequestDto, 'shiftNeedId'>
  ): Observable<ShiftRequestResponse> {
    const payload: CreateShiftRequestDto = { shiftNeedId, ...request };

    return this.http.post<ShiftRequestResponse>(
      `${this.apiUrl}/${shiftNeedId}/request`,
      payload
    );
  }

  // ============================================================================
  // SHIFT REQUEST METHODS (Facility-Initiated)
  // ============================================================================

  /**
   * Get facility-initiated shift requests for the current contractor
   * Falls back to mock data if API fails
   */
  getShiftRequests(
    searchDto?: ShiftRequestSearchDto
  ): Observable<DashboardShiftRequestDto[]> {
    const params = HttpHelper.toHttpParams(searchDto);

    return this.http
      .get<
        PagedResultDto<DashboardShiftRequestDto>
      >(`${this.contractorsApi}/me/shift-requests`, { params })
      .pipe(
        map((result) => result.items),
        catchError(() => {
          return of(MOCK_SHIFT_REQUESTS);
        })
      );
  }

  /**
   * Get contractor-initiated shift requests (applications)
   * Returns shifts where the contractor has applied (pending or accepted)
   * Falls back to empty array if API fails
   */
  getMyApplications(
    searchDto?: ShiftRequestSearchDto
  ): Observable<PagedResultDto<DashboardShiftRequestDto>> {
    const params = HttpHelper.toHttpParams({
      ...searchDto
    });

    return this.http
      .get<
        PagedResultDto<DashboardShiftRequestDto>
      >(`${this.contractorsApi}/me/applications`, { params })
      .pipe(
        catchError(() => {
          return of({
            items: [],
            totalCount: 0,
            pageNumber: 1,
            pageSize: 100,
            totalPages: 0,
            hasPreviousPage: false,
            hasNextPage: false
          });
        })
      );
  }

  /**
   * Accept a facility-initiated shift request
   */
  acceptShiftRequest(
    requestId: string,
    message?: string
  ): Observable<DashboardShiftRequestDto> {
    return this.http.post<DashboardShiftRequestDto>(
      `${this.contractorsApi}/me/shift-requests/${requestId}/accept`,
      { message }
    );
  }

  /**
   * Decline a facility-initiated shift request
   */
  declineShiftRequest(
    requestId: string,
    reason?: string
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.contractorsApi}/me/shift-requests/${requestId}/decline`,
      { reason }
    );
  }

  /**
   * Contractor submits a counter-offer on an urgent shift request.
   * POST /api/contractors/me/shift-requests/{requestId}/counter-offer
   */
  submitCounterOffer(
    requestId: string,
    body: CounterOfferRequestDto
  ): Observable<CounterOfferDto> {
    return this.http.post<CounterOfferDto>(
      `${this.contractorsApi}/me/shift-requests/${requestId}/counter-offer`,
      body
    );
  }

  /**
   * Fetch the full negotiation history for a shift request.
   * GET /api/shift-requests/{requestId}/negotiation-history
   */
  getNegotiationHistory(requestId: string): Observable<NegotiationHistoryDto> {
    return this.http.get<NegotiationHistoryDto>(
      `/api/shift-requests/${requestId}/negotiation-history`
    );
  }

  /**
   * Get the contractor's shift request for a specific shift need.
   * Searches both facility-initiated requests and contractor applications.
   * Returns the first match or null.
   */
  getMyRequestForShiftNeed(
    shiftNeedId: string
  ): Observable<DashboardShiftRequestDto | null> {
    const search: ShiftRequestSearchDto = {
      shiftNeedId,
      pageSize: 1,
      pageNumber: 1
    };

    return this.getShiftRequests(search).pipe(
      switchMap((items) => {
        if (items.length > 0) return of(items[0]);
        // No facility-initiated request found, try contractor applications
        return this.getMyApplications(search).pipe(
          map((result) => result.items[0] ?? null)
        );
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Mock data fallback for development/testing
   */
  private getMockShiftNeeds(
    searchDto: ShiftNeedSearchDto
  ): PagedResultDto<ShiftNeed> {
    let filtered = [...MOCK_SHIFT_NEEDS];

    // Apply filters
    if (searchDto.searchTerm) {
      const searchLower = searchDto.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (shift) =>
          shift.facilityName.toLowerCase().includes(searchLower) ||
          shift.scopeOfPracticeDisplay.toLowerCase().includes(searchLower)
      );
    }

    if (searchDto.scopesOfPractice && searchDto.scopesOfPractice.length > 0) {
      filtered = filtered.filter((shift) =>
        searchDto.scopesOfPractice!.includes(
          shift.scopeOfPractice as ScopeOfPracticeEnum
        )
      );
    }

    if (searchDto.shiftTypes && searchDto.shiftTypes.length > 0) {
      filtered = filtered.filter((shift) =>
        searchDto.shiftTypes!.includes(shift.shiftType as ShiftTypeEnum)
      );
    }

    if (searchDto.startDate) {
      filtered = filtered.filter(
        (shift) => shift.shiftDate >= searchDto.startDate!
      );
    }

    if (searchDto.endDate) {
      filtered = filtered.filter(
        (shift) => shift.shiftDate <= searchDto.endDate!
      );
    }

    if (searchDto.minHourlyRate) {
      filtered = filtered.filter(
        (shift) =>
          shift.hourlyRate && shift.hourlyRate >= searchDto.minHourlyRate!
      );
    }

    if (searchDto.maxHourlyRate) {
      filtered = filtered.filter(
        (shift) =>
          shift.hourlyRate && shift.hourlyRate <= searchDto.maxHourlyRate!
      );
    }

    // Pagination
    const pageNumber = searchDto.pageNumber || 1;
    const pageSize = searchDto.pageSize || 20;
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filtered.slice(startIndex, endIndex);

    return {
      items: paginatedItems,
      totalCount: filtered.length,
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filtered.length / pageSize),
      hasNextPage: endIndex < filtered.length,
      hasPreviousPage: pageNumber > 1
    };
  }
}
